import { useEffect, useState } from 'react';
import { useSubmissions } from '@/hooks/useSubmissions';
import type { Incident } from '@/types';

export default function AdminIncidentes() {
  const { incidents, counts, updateIncidentStatus } = useSubmissions();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Incident | null>(null);
  const [statusDraft, setStatusDraft] = useState<Incident['status']>('Em análise');
  const [reasonDraft, setReasonDraft] = useState('');

  useEffect(() => {
    if (selected) {
      setStatusDraft(selected.status);
      setReasonDraft(selected.statusReason || '');
    }
  }, [selected]);

  const filtered = incidents.filter((r) => {
    return (
      (r.displayId || r.id).toLowerCase().includes(search.toLowerCase()) ||
      r.userName.toLowerCase().includes(search.toLowerCase()) ||
      r.setor.toLowerCase().includes(search.toLowerCase()) ||
      r.tipoIncidente.toLowerCase().includes(search.toLowerCase()) ||
      r.descricao.toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-portal-gray-900">Incidentes Registrados</h1>
        <p className="text-sm text-portal-gray-500">
          Visualize e gerencie os incidentes registrados pelos colaboradores.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {[
          { label: 'Total de Incidentes', value: counts.incident, color: 'bg-portal-purple text-white' },
          { label: 'No Setor', value: incidents.filter((i) => i.noSetor === 'Sim').length, color: 'bg-portal-purple-100 text-portal-purple' },
          { label: 'Fora do Setor', value: incidents.filter((i) => i.noSetor !== 'Sim').length, color: 'bg-portal-gray-100 text-portal-gray-700' },
        ].map((s) => (
          <div key={s.label} className={`${s.color} rounded-xl p-4`}>
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-xs font-medium opacity-90">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-portal-gray-200 p-4 mb-4">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-portal-gray-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, setor, tipo ou descrição..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-portal-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple"
          />
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-portal-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-portal-gray-100 flex items-center justify-between">
          <span className="text-sm font-medium text-portal-gray-700">
            Incidentes ({filtered.length})
          </span>
        </div>
        {filtered.length === 0 ?(
          <div className="p-12 text-center">
            <i className="ri-inbox-line text-portal-gray-300 text-3xl mb-3 block"></i>
            <p className="text-sm text-portal-gray-500">Nenhum incidente encontrado.</p>
          </div>
        ) : (
          <div className="divide-y divide-portal-gray-100">
            {filtered.map((r) => (
              <div key={r.id} className="px-5 py-4 hover:bg-portal-gray-50/50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-mono text-portal-gray-500">
                        {r.displayId || r.id}
                      </span>
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                        {r.tipoIncidente}
                      </span>
                      <span className="text-xs text-portal-gray-400">
                        {new Date(r.createdAt).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-portal-gray-800 mb-1">{r.userName} — {r.setor}</p>
                    <p className="text-sm text-portal-gray-600 line-clamp-2">{r.descricao}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-portal-gray-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-line"></i>
                        {r.noSetor === 'Sim' ?'No setor' : 'Fora do setor'}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-shield-check-line"></i>
                        {r.declaracaoVerdade ?'Declaração confirmada' : 'Declaração pendente'}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelected(r)}
                    className="flex-shrink-0 text-portal-purple hover:text-portal-purple-dark text-xs font-medium cursor-pointer whitespace-nowrap"
                  >
                    Ver detalhes
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)}></div>
          <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-portal-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-portal-gray-800">Detalhes do Incidente</h3>
              <button
                onClick={() => setSelected(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-portal-gray-400 hover:text-portal-gray-600 hover:bg-portal-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">ID</p>
                  <p className="text-sm font-mono text-portal-gray-700">{selected.displayId || selected.id}</p>
                </div>
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Colaborador</p>
                  <p className="text-sm font-medium text-portal-gray-800">{selected.userName}</p>
                </div>
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Setor</p>
                  <p className="text-sm font-medium text-portal-gray-800">{selected.setor}</p>
                </div>
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Tipo</p>
                  <p className="text-sm font-medium text-red-700">{selected.tipoIncidente}</p>
                </div>
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Ocorreu no setor?</p>
                  <p className="text-sm text-portal-gray-700">{selected.noSetor}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-portal-gray-400 mb-1">Descrição</p>
                <p className="text-sm text-portal-gray-700 bg-portal-gray-50 rounded-lg p-3">{selected.descricao}</p>
              </div>
              <div className="rounded-xl border border-portal-gray-200 p-4 space-y-3">
                <div>
                  <label className="text-xs text-portal-gray-500 mb-1 block">Alterar status</label>
                  <select
                    value={statusDraft}
                    onChange={(e) => setStatusDraft(e.target.value as Incident['status'])}
                    className="w-full text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-700 focus:outline-none focus:ring-2 focus:ring-portal-purple/20"
                  >
                    <option value="Em análise">Em análise</option>
                    <option value="Concluído">Concluído</option>
                  </select>
                </div>
                {statusDraft === 'Concluído' && (
                  <textarea
                    value={reasonDraft}
                    onChange={(e) => setReasonDraft(e.target.value)}
                    placeholder="Informe a justificativa/conclusão"
                    className="w-full min-h-24 text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-700 focus:outline-none focus:ring-2 focus:ring-portal-purple/20"
                  />
                )}
                <button
                  type="button"
                  onClick={async () => {
                    if (statusDraft === 'Concluído' && !reasonDraft.trim()) return;
                    try {
                      await updateIncidentStatus(selected.id, statusDraft, reasonDraft.trim());
                      setSelected(null);
                    } catch (err) {
                      alert(err instanceof Error ? err.message : 'Não foi possível salvar o status.');
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-portal-purple rounded-lg hover:bg-portal-purple-dark disabled:opacity-50"
                  disabled={statusDraft === 'Concluído' && !reasonDraft.trim()}
                >
                  Salvar status
                </button>
              </div>
              <div className="flex items-center gap-2 text-xs text-portal-gray-500">
                <i className="ri-calendar-line"></i>
                Registrado em {new Date(selected.createdAt).toLocaleDateString('pt-BR')}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-portal-gray-100 flex justify-end">
              <button
                onClick={() => setSelected(null)}
                className="px-4 py-2 text-sm font-medium text-portal-gray-600 bg-white border border-portal-gray-200 rounded-lg hover:bg-portal-gray-50 cursor-pointer whitespace-nowrap"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
